import {Box, Skeleton} from "@mui/material"
import {FC} from "react"
import {ExpertDetailCard} from "../../molecules/expertDetailCard"
import {ExpertsNoResults} from "../../molecules/ExpertsNoResults"
import {useClientSessionStore} from "../../../stores/clientSessionStore"
import {expertsEndpoints} from "../../../services/api/experts"
import {useConfigProvider} from "../../../config"
import {useQuery} from "@tanstack/react-query"
import {ExpertModel} from "../../../types/ExpertModel"
import {Account, AccountTeam} from "../../../types"
import {expertListStore} from "../../../stores/expertListStore"

type Props = {
  parentRef?: any
}

const ExpertList: FC<Props> = (props) => {
  const {getExperts, searchExperts} = expertsEndpoints()
  const {cmsImageUrl} = useConfigProvider()
  const {
    expertList,
    searchExpertList,
    searchTerm,
    regions,
    setExpert,
    setSearchExpert,
  } = expertListStore()

  const {
    refetch,
    isLoading,
    isFetching,
    isRefetching,
    isError,
    isLoadingError,
    isRefetchError,
  } = useQuery({
    queryKey: ["getExperts"],
    queryFn: async ({signal}) => {
      const experts = await getExperts(999, 0, signal)
      setExpert(experts)
      return experts
    },
  })

  const {
    refetch: searchExpertsRefetch,
    isLoading: searchExpertsIsLoading,
    isFetching: searchExpertsIsFetching,
    isRefetching: searchExpertsIsRefetching,
    isError: searchExpertsIsError,
    isLoadingError: searchExpertsIsLoadingError,
    isRefetchError: searchExpertsIsRefetchError,
  } = useQuery({
    queryKey: ["searchExperts", searchTerm, regions],
    queryFn: async () => {
      const filteredExperts = await searchExperts(searchTerm, regions)
      setSearchExpert(filteredExperts)
      return filteredExperts
    },
    enabled: !!searchTerm || !!regions,
  })

  const sessionClient = useClientSessionStore((state) => state.crmAccount)

  const searchTextLoading =
    searchTerm || regions
      ? searchExpertsIsLoading ||
        searchExpertsIsRefetching ||
        searchExpertsIsFetching
      : false

  const searchTextError =
    searchTerm || regions
      ? searchExpertsIsError ||
        searchExpertsIsLoadingError ||
        searchExpertsIsRefetchError
      : false

  return (
    <Box>
      {!sessionClient ||
      isLoading ||
      isFetching ||
      isRefetching ||
      searchTextLoading ? (
        [1, 2, 3, 4, 5, 6]?.map((item, index) => (
          <Box
            flexDirection={"row"}
            display={"flex"}
            width={"100%"}
            marginBottom={"24px"}
            paddingRight={"40px"}
          >
            <Box
              flexDirection="column"
              display="flex"
              width="17%"
              justifyContent="center"
              alignItems="end"
              paddingRight="8px"
            >
              <Skeleton
                variant="circular"
                width={56}
                height={56}
                style={{
                  borderRadius: "50%",
                }}
              />
            </Box>
            <Box flexDirection="column" display="flex" width="60%">
              <Box flexDirection="row" display="flex" width="100%">
                <Box flexDirection="column" display="flex" width="100%">
                  <Skeleton variant="text" height={18} />
                </Box>
                <Box flexDirection="column" display="flex" width="25%">
                  <Skeleton
                    variant="text"
                    height={24}
                    width={24}
                    style={{
                      alignSelf: "end",
                      marginTop: "-3px",
                    }}
                  />
                </Box>
              </Box>
              <Box flexDirection="row" display="flex">
                <Skeleton variant="text" height={13} width="40%" />
              </Box>
              <Box flexDirection="row" display="flex">
                <Skeleton variant="text" height={13} width="40%" />
              </Box>
            </Box>
            <Box
              flexDirection="column"
              display="flex"
              justifyContent="start"
              width="23%"
              paddingLeft="24px"
            >
              <Box flexDirection="row" display="flex">
                <Box
                  flexDirection="column"
                  display="flex"
                  width="100%"
                  alignItems="end"
                  justifyContent="center"
                  paddingRight="4px"
                >
                  <Skeleton
                    variant="text"
                    height={22}
                    width={56}
                    sx={{
                      marginTop: "-3px",
                    }}
                  />
                </Box>
                <Box
                  flexDirection="column"
                  display="flex"
                  width="100%"
                  alignItems="end"
                >
                  <Skeleton
                    variant="text"
                    height={24}
                    width={24}
                    sx={{
                      marginTop: "-3px",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        ))
      ) : isError || isLoadingError || isRefetchError || searchTextError ? (
        <ExpertsNoResults
          isError={true}
          refetch={
            isError || isLoadingError || isRefetchError
              ? refetch
              : searchExpertsRefetch
          }
        />
      ) : searchTerm || searchTerm !== "" || regions ? (
        searchExpertList && searchExpertList?.length > 0 ? (
          searchExpertList?.map((expert: ExpertModel, i: number) => (
            <Box
              flexDirection="column"
              display="flex"
              width="100%"
              sx={{
                width: "100%",
              }}
            >
              <ExpertDetailCard
                selectedMember={
                  {
                    accountTeamEmailAddress: expert.email,
                    accountTeamFullName: expert.name,
                    accountTeamId: "",
                    crmAccountId: "",
                    crmAccountName: "",
                    crmAccountNumber: "",
                  } as AccountTeam
                }
                contactData={
                  {
                    crmAccountId: "",
                    crmAccountURL: "",
                    crmAccountName: "",
                    parentAccountName: "",
                    parentAccountId: "",
                    country: "",
                    addressline1: "",
                    addressline2: "",
                    addressline3: "",
                    city: "",
                    stateorProvince: "",
                    postalCode: "",
                    telephoneNumber: "",
                    accountNumber: "",
                    relationshipManager: "",
                    relationshipManagerEmail: "",
                    lastUpdatedDateTime: "",
                    myControlRisksAccess: true,
                  } as Account
                }
                contactTeamData={[
                  {
                    accountTeamEmailAddress: expert.email,
                    accountTeamFullName: expert.name,
                    accountTeamId: "",
                    crmAccountId: "",
                    crmAccountName: "",
                    crmAccountNumber: "",
                  },
                ]}
                authorId={expert.group}
                authorName={expert.title}
                imagePath={cmsImageUrl + expert.imageSmall}
                location={expert.locationHierarchy}
                tags={expert.tagTitles}
              />
            </Box>
          ))
        ) : (
          <ExpertsNoResults isError={isError} refetch={searchExpertsRefetch} />
        )
      ) : expertList && expertList?.length > 0 ? (
        expertList?.map((expert: ExpertModel, i: number) => (
          <Box
            flexDirection="column"
            display="flex"
            width="100%"
            sx={{
              width: "100%",
            }}
          >
            <ExpertDetailCard
              parentRef={props.parentRef}
              selectedMember={
                {
                  accountTeamEmailAddress: expert.email,
                  accountTeamFullName: expert.name,
                  accountTeamId: "",
                  crmAccountId: "",
                  crmAccountName: "",
                  crmAccountNumber: "",
                } as AccountTeam
              }
              contactData={
                {
                  crmAccountId: "",
                  crmAccountURL: "",
                  crmAccountName: "",
                  parentAccountName: "",
                  parentAccountId: "",
                  country: "",
                  addressline1: "",
                  addressline2: "",
                  addressline3: "",
                  city: "",
                  stateorProvince: "",
                  postalCode: "",
                  telephoneNumber: "",
                  accountNumber: "",
                  relationshipManager: "",
                  relationshipManagerEmail: "",
                  lastUpdatedDateTime: "",
                  myControlRisksAccess: true,
                } as Account
              }
              contactTeamData={[
                {
                  accountTeamEmailAddress: expert.email,
                  accountTeamFullName: expert.name,
                  accountTeamId: "",
                  crmAccountId: "",
                  crmAccountName: "",
                  crmAccountNumber: "",
                },
              ]}
              authorId={expert.group}
              authorName={expert.title}
              imagePath={cmsImageUrl + expert.imageSmall}
              location={expert.locationHierarchy}
              tags={expert.tagTitles}
            />
          </Box>
        ))
      ) : (
        <ExpertsNoResults isError={isError} refetch={refetch} />
      )}
    </Box>
  )
}

export default ExpertList
